/**
 * Fantasy Console 0 - Random Number Generator
 * Seeded RNG using Linear Congruential Generator (LCG)
 */

/**
 * RandomGenerator class - provides seeded random number generation
 * Uses LCG algorithm with parameters similar to glibc
 */
export class RandomGenerator {
  // LCG parameters (glibc-style)
  private static readonly A = 1103515245
  private static readonly C = 12345
  private static readonly M = 2147483648 // 2^31

  private seed: number

  constructor() {
    // Initialize with a random seed based on current time
    this.seed = Date.now() % RandomGenerator.M
  }

  /**
   * Set the random seed
   * Using the same seed produces the same sequence of random numbers
   */
  srand(seed: number): void {
    // Ensure seed is a valid integer
    this.seed = Math.floor(Math.abs(seed)) % RandomGenerator.M
  }

  /**
   * Generate a random number
   * @param x Optional upper bound (exclusive)
   * @returns Random number in range [0, x) if x provided, otherwise [0, 1)
   */
  rnd(x?: number): number {
    // Advance the LCG
    this.seed =
      (RandomGenerator.A * this.seed + RandomGenerator.C) % RandomGenerator.M

    // Convert to float in range [0, 1)
    const normalized = this.seed / RandomGenerator.M

    // If x is provided, scale to [0, x)
    if (x !== undefined) {
      return normalized * x
    }

    return normalized
  }
}

// Global random generator instance
const globalRandom = new RandomGenerator()

/**
 * Generate a random number
 * @param x Optional upper bound (exclusive)
 * @returns Random number in range [0, x) if x provided, otherwise [0, 1)
 */
export function rnd(x?: number): number {
  return globalRandom.rnd(x)
}

/**
 * Set the random seed
 * Using the same seed produces the same sequence of random numbers
 * @param seed Integer seed value
 */
export function srand(seed: number): void {
  globalRandom.srand(seed)
}
